// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.narnia-farms.com/api/v1';

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    SIGNIN: '/auth/signin',
    SIGNOUT: '/auth/signout',
    SIGNOUT_ALL: '/auth/signout-all',
    REFRESH: '/auth/refresh',
    PROFILE: '/auth/profile',
  },

  // Dashboard
  DASHBOARD: {
    OVERVIEW: '/dashboard/overview',
    METRICS: '/dashboard/metrics',
    ALERTS: '/dashboard/alerts',
    BIRD_OVERVIEW: (birdId: string | number) => `/dashboard/bird/${birdId}/overview`,
    DAILY_OVERVIEW: '/dashboard/daily-overview',
  },

  // Financial Cycles
  FINANCIAL_CYCLES: {
    LIST: '/financial-cycles',
    CURRENT: '/financial-cycles/current',
    SUMMARY: '/financial-cycles/summary',
    UPCOMING: '/financial-cycles/upcoming',
    TRENDS: '/financial-cycles/trends',
    DETAIL: (id: string | number) => `/financial-cycles/${id}`,
    CLOSE: '/financial-cycles/close',
    PROCUREMENT_BIRDS: (cycleId: number, procurementId: number) =>
      `/financial-cycles/${cycleId}/procurement-performance/${procurementId}/birds`,
  },

  // Birds
  BIRDS: {
    LIST: '/birds',
    CREATE: '/birds',
    DETAIL: (id: string | number) => `/birds/${id}`,
    STATS: '/birds/stats',
    QUANTITIES: '/birds/quantities',
    START_LAYING: (id: string | number) => `/birds/${id}/start-laying`,
    LOGS: (id: string | number) => `/birds/${id}/logs`,
    UPDATE_FEED_INFO: (id: string | number) => `/birds/${id}/update-feed-info`,
    TYPES: '/birds/types',
    CALCULATE: (id: string | number) => `/feed/${id}/calculate`,
    BLOCKS: (id: string | number) => `/birds/${id}/blocks`,
    BLOCK: (birdId: string | number, blockId: string | number) =>
      `/birds/${birdId}/blocks/${blockId}`,
    BLOCKS_FEED: (id: string | number) => `/birds/${id}/blocks/feed`,
    WEIGHT_SAMPLES: (id: string | number) => `/birds/${id}/weight-samples`,
    WEIGHT_SAMPLES_TREND: (id: string | number) => `/birds/${id}/weight-samples/trend`,
    WEIGHT_SAMPLES_WEEK: (birdId: string | number, week: number) =>
      `/birds/${birdId}/weight-samples/${week}`,
    WEIGHT_SAMPLES_EXTRACT: (id: string | number) => `/birds/${id}/weight-samples/extract-image`,
    WEIGHT_SAMPLES_REGROUP: (id: string | number) => `/birds/${id}/weight-samples-regroup`,
    WEIGHT_SUBMISSIONS: '/birds/weight-submissions',
    WEIGHT_SUBMISSION: (id: number) => `/birds/weight-submissions/${id}`,
    WEIGHT_SUBMISSION_EXTRACT: (id: number) => `/birds/weight-submissions/${id}/extract`,
    WEIGHT_SUBMISSION_RECORD: (id: number) => `/birds/weight-submissions/${id}/record`,
    WEIGHT_SUBMISSION_IMAGE: (submissionId: number, imageId: number) =>
      `/birds/weight-submissions/${submissionId}/images/${imageId}`,
    WEIGHT_SUBMISSION_IMAGE_REANALYSE: (submissionId: number, imageId: number) =>
      `/birds/weight-submissions/${submissionId}/images/${imageId}/reanalyse`,
  },

  // Bird Logs
  BIRD_LOGS: {
    LIST: '/bird-logs/all',
    CREATE: '/bird-logs',
    DETAIL: (id: string | number) => `/bird-logs/${id}`,
    DELETE: (id: string | number) => `/bird-logs/${id}`,
  },

  // Bird Procurement Assignees
  BIRD_PROCUREMENT_ASSIGNEES: {
    LIST: '/bird-procurement-assignees',
    CREATE: '/bird-procurement-assignees',
    MY_PROCUREMENTS: '/bird-procurement-assignees/my-procurements',
    STATS: '/bird-procurement-assignees/stats',
    DELETE: (id: string | number) => `/bird-procurement-assignees/${id}`,
    BY_PROCUREMENT: (id: string | number) => `/bird-procurement-assignees/procurement/${id}`,
  },

  // Bird Batches
  BIRD_BATCHES: {
    LIST: '/bird-batches',
    DETAIL: (id: string | number) => `/bird-batches/${id}`,
    UPDATE: (id: string | number) => `/bird-batches/${id}`,
    EMPLOYEES: (batchId: string | number) => `/bird-batches/${batchId}/employees`,
    RELEASE_EMPLOYEE: (batchId: string | number, employeeId: string | number) =>
      `/bird-batches/${batchId}/employees/${employeeId}`,
  },

  // Employee Batches
  EMPLOYEE_BATCHES: {
    LIST: (employeeId: string | number) => `/employees/${employeeId}/batches`,
  },

  // Bird Procurements
  BIRD_PROCUREMENTS: {
    LIST: '/bird-procurements',
    CREATE: '/bird-procurements',
    DETAIL: (id: string | number) => `/bird-procurements/${id}`,
    STATS: '/bird-procurements/stats',
    SUPPLIERS: '/bird-procurements/suppliers',
    MARK_DELIVERED: (id: string | number) => `/bird-procurements/${id}/mark-delivered`,
  },

  // Egg Production
  EGG_PRODUCTIONS: {
    LIST: '/egg-productions',
    CREATE: '/egg-productions',
    DETAIL: (id: string | number) => `/egg-productions/${id}`,
    STATS: '/egg-productions/stats',
    TRENDS: '/egg-productions/trends',
    TOP_PERFORMANCE: '/egg-productions/top-performance',
    DAY_TO_DAY: (birdId: string | number) => `/egg-productions/bird/${birdId}/day-to-day`,
    CORRELATION: '/egg-productions/correlation',
    PRODUCTION_CURVE: '/egg-productions/production-curve',
  },

  // Egg Sales
  EGG_SALES: {
    LIST: '/egg-sales',
    CREATE: '/egg-sales',
    BATCH: '/egg-sales/batch',
    BULK_MARK_PAID: '/egg-sales/bulk-mark-paid',
    DETAIL: (id: string | number) => `/egg-sales/${id}`,
    STATS: '/egg-sales/stats',
    PENDING: '/egg-sales/pending',
    TRENDS: '/egg-sales/trends',
    MARK_PAID: (id: string | number) => `/egg-sales/${id}/mark-paid`,
  },

  // Broken Egg Sales
  BROKEN_EGG_SALES: {
    LIST: '/broken-egg-sales',
    CREATE: '/broken-egg-sales',
    STOCK: '/broken-egg-sales/stock',
  },

  // Deposits
  DEPOSITS: {
    LIST: '/deposits',
    CREATE: '/deposits',
    BULK_COMPLETE: '/deposits/bulk-complete',
    REVERSE: '/deposits/reverse',
    CORRECT: '/deposits/correct',
    DETAIL: (id: string | number) => `/deposits/${id}`,
    STATS: '/deposits/stats',
    SALE_DEPOSITS: (eggSaleId: string | number) => `/deposits/sale/${eggSaleId}`,
  },

  // Egg Stock
  EGG_STOCK: {
    LIST: '/egg-stock',
    SUMMARY: '/egg-stock/summary',
    ALERTS: '/egg-stock/alerts',
    BY_PROCUREMENT: (id: string | number) => `/egg-stock/procurement/${id}`,
  },

  // Egg Prices
  EGG_PRICES: {
    LIST: '/egg-prices',
    CREATE: '/egg-prices',
    CURRENT: '/egg-prices/current',
    HISTORY: (birdId: string | number) => `/egg-prices/history/${birdId}`,
    SET_CURRENT: (id: string | number) => `/egg-prices/${id}/set-current`,
  },

  // Customers
  CUSTOMERS: {
    LIST: '/customers',
    CREATE: '/customers',
    DETAIL: (id: string | number) => `/customers/${id}`,
    STATS: '/customers/stats',
    OUTSTANDING: '/customers/outstanding',
    LOYAL: '/customers/loyal',
    SEARCH: '/customers/search',
    PURCHASE_HISTORY: (id: string | number) => `/customers/${id}/purchase-history`,
    RESEND_SMS: (id: string | number) => `/customers/${id}/resend-sms`,
    SMS_LOGS: '/customers/sms-logs',
  },

  // Expenditures
  EXPENDITURES: {
    LIST: '/expenditures',
    ALL: '/expenditures/all',
    CREATE: '/expenditures',
    DETAIL: (id: string | number) => `/expenditures/${id}`,
    STATS: '/expenditures/stats',
    PENDING: '/expenditures/pending',
    TRENDS: '/expenditures/trends',
    MAKE_PAYMENT: (id: string | number) => `/expenditures/${id}/make-payment`,
  },

  // Expenditure Categories
  EXPENDITURE_CATEGORIES: {
    LIST: '/expenditure-categories',
    CREATE: '/expenditure-categories',
    DETAIL: (id: string | number) => `/expenditure-categories/${id}`,
    STATS: '/expenditure-categories/stats',
  },

  // Mortalities
  MORTALITIES: {
    LIST: '/mortalities/list',
    CREATE: '/mortalities',
    DETAIL: (id: string | number) => `/mortalities/${id}`,
    STATS: '/mortalities/stats',
    TRENDS: '/mortalities/trends',
  },

  // Decommissions
  DECOMMISSIONS: {
    LIST: '/decommissions',
    CREATE: '/decommissions',
    DETAIL: (id: string | number) => `/decommissions/${id}`,
    STATS: '/decommissions/stats',
    TRENDS: '/decommissions/trends',
  },

  // Feed Management
  FEED: {
    DASHBOARD: '/feed/dashboard',
    TYPES: '/feed/types',
    BRANDS: '/feed/brands',
    STOCK: '/feed/stock',
    CALCULATE: '/feed/calculate',
    STOCK_ALERTS: '/feed/stock/alerts',
    PROCUREMENTS: '/feed/procurements',
    ASSIGNMENTS: '/feed/assignments',
    ASSIGN_TO_BATCH: '/feed/assign-to-batch',
    TRENDS: '/feed/trends',
    COST_ANALYSIS: '/feed/cost-analysis',
    RAW_MATERIAL_PROCUREMENT_PAYMENTS: (id: number | string) =>
      `/feed/raw-materials/procurements/${id}/payments`,
    RAW_MATERIAL_PROCUREMENT_PAYMENT: (id: number | string, paymentId: number | string) =>
      `/feed/raw-materials/procurements/${id}/payments/${paymentId}`,
    RAW_MATERIAL_PROCUREMENT: (id: number | string) => `/feed/raw-materials/procurements/${id}`,
    SUPPLIERS: '/feed/suppliers',
    SUPPLIER: (id: number | string) => `/feed/suppliers/${id}`,
    SUPPLIER_TOGGLE: (id: number | string) => `/feed/suppliers/${id}/toggle-status`,
    FCR: '/feed/fcr',
  },

  // Bird Weights
  BIRD_WEIGHTS: {
    LIST: '/bird-weights',
    CREATE: '/bird-weights',
    DETAIL: (id: string | number) => `/bird-weights/${id}`,
    UPDATE: (id: string | number) => `/bird-weights/${id}`,
    DELETE: (id: string | number) => `/bird-weights/${id}`,
    STATUS: (id: string | number) => `/bird-weights/${id}/status`,
    BIRD_HISTORY: (birdId: string | number) => `/bird-weights/bird/${birdId}/history`,
    BIRD_LATEST: (birdId: string | number) => `/bird-weights/bird/${birdId}/latest`,
    BIRD_STATS: (birdId: string | number) => `/bird-weights/bird/${birdId}/stats`,
    BIRD_STATUS: (birdId: string | number) => `/bird-weights/bird/${birdId}/status`,
  },

  // Requests
  REQUESTS: {
    LIST: '/requests',
    CREATE: '/requests',
    DETAIL: (id: string | number) => `/requests/${id}`,
    STATS: '/requests/stats',
    ASSIGN: (id: string | number) => `/requests/${id}/assign`,
    IN_PROGRESS: (id: string | number) => `/requests/${id}/in-progress`,
    COMPLETE: (id: string | number) => `/requests/${id}/complete`,
  },

  // Misc
  MISC: {
    USERS: '/misc/users',
    PROCUREMENTS: '/misc/procurements',
    CUSTOMERS: '/misc/customers',
    LAYING_PROCUREMENTS: '/misc/laying-procurements',
  },

  SECURITY: {
    USERS: '/security/users',
    SUPERVISORS: '/security/supervisors',
    PERMISSIONS: '/security/permissions',
    USER_PERMISSIONS: (userId: string | number) => `/security/users/${userId}/permissions`,
    ASSIGN_PERMISSIONS: (userId: string | number) => `/security/users/${userId}/permissions`,
    GRANT_PERMISSION: (userId: string | number) => `/security/users/${userId}/permissions/grant`,
    REVOKE_PERMISSION: (userId: string | number, permissionId: string | number) =>
      `/security/users/${userId}/permissions/${permissionId}`,
    RESOURCE_PERMISSIONS: (userId: string | number) =>
      `/security/users/${userId}/permissions/resource`,
    STATISTICS: '/security/statistics',
    TOGGLE_ACTIVE: (userId: string | number) => `/security/users/${userId}/toggle-active`,
  },

  // Crate Stocks
  CRATE_STOCKS: {
    LIST: '/crate-stocks',
    CREATE: '/crate-stocks',
    CREATE_STOCK_ONLY: '/crate-stocks/stock-only',
    DETAIL: (id: string | number) => `/crate-stocks/${id}`,
    STATS: '/crate-stocks/stats',
    HISTORY: '/crate-stocks/history',
  },

  // Alerts
  ALERTS: {
    LIST: '/alerts',
    COUNT: '/alerts/count',
  },

  // Vet Visit Reports
  VET_VISIT_REPORTS: {
    LIST: '/vet-visit-reports',
    DETAIL: (id: number | string) => `/vet-visit-reports/${id}`,
  },

  // Vet Instructions
  VET_INSTRUCTIONS: {
    LIST: '/vet-instructions',
    CREATE: '/vet-instructions',
    SHOW: (id: number) => `/vet-instructions/${id}`,
    COMPLETE: (id: number) => `/vet-instructions/${id}/complete`,
  },

  // Cash Flow
  CASH_FLOW: {
    PROJECTION: '/cash-flow',
  },

  // Financial Summary
  FINANCIAL_SUMMARY: {
    MONTHLY: '/financial-summary/monthly',
  },

  // Capital Investments
  CAPITAL_INVESTMENTS: {
    LIST: '/capital-investments',
    DETAIL: (id: number | string) => `/capital-investments/${id}`,
  },

  // Supply Procurements
  SUPPLY_PROCUREMENTS: {
    LIST: '/feed/supply-procurements',
    DETAIL: (id: number | string) => `/feed/supply-procurements/${id}`,
    PAYMENT: (id: number | string) => `/feed/supply-procurements/${id}/make-payment`,
  },

  // Vaccinations
  VACCINATIONS: {
    ALERTS: '/vaccination-alerts',
    SCHEDULE: '/vaccination-schedule',
    MARK_COMPLETED: (procurementId: string | number) =>
      `/procurements/${procurementId}/vaccinations`,
  },

  // Preventive Health
  PREVENTIVE_HEALTH: {
    PROGRAMMES: '/preventive-health/programmes',
    PROGRAMME: (id: number | string) => `/preventive-health/programmes/${id}`,
    PROGRAMME_EVENTS: (id: number | string) => `/preventive-health/programmes/${id}/events`,
    PROGRAMME_EVENT: (id: number | string) => `/preventive-health/programme-events/${id}`,
    PROGRAMME_REGENERATE: (id: number | string) => `/preventive-health/programmes/${id}/regenerate`,
    BATCH_SCHEDULE: (id: number | string) => `/preventive-health/batches/${id}/schedule`,
    BATCH_UPCOMING: (id: number | string) => `/preventive-health/batches/${id}/schedule/upcoming`,
    BATCH_REGENERATE: (id: number | string) =>
      `/preventive-health/batches/${id}/regenerate-schedule`,
    BATCH_ASSIGN_PROGRAMME: (id: number | string) =>
      `/preventive-health/batches/${id}/assign-programme`,
    TASKS_TODAY: '/preventive-health/tasks/today',
    TASKS_UPCOMING: '/preventive-health/tasks/upcoming',
    EVENT_START: (id: number | string) => `/preventive-health/scheduled-events/${id}/start`,
    EVENT_COMPLETE: (id: number | string) => `/preventive-health/scheduled-events/${id}/complete`,
    EVENT_SKIP: (id: number | string) => `/preventive-health/scheduled-events/${id}/skip`,
    EVENT_PHOTOS: (id: number | string) => `/preventive-health/scheduled-events/${id}/photos`,
    EVENT_ASSIGN: (id: number | string) => `/preventive-health/scheduled-events/${id}/assign`,
    ALERTS: '/preventive-health/alerts',
    ALERT_ACKNOWLEDGE: (id: number | string) => `/preventive-health/alerts/${id}/acknowledge`,
    ALERT_RESOLVE: (id: number | string) => `/preventive-health/alerts/${id}/resolve`,
    ALERT_THRESHOLDS: '/preventive-health/alert-thresholds',
    ALERT_THRESHOLD: (id: number | string) => `/preventive-health/alert-thresholds/${id}`,
    PRODUCTS: '/preventive-health/products',
    PRODUCT: (id: number | string) => `/preventive-health/products/${id}`,
    REPORT_WITHDRAWAL: '/preventive-health/reports/withdrawal-status',
    REPORT_COMPLIANCE: '/preventive-health/reports/compliance',
    REPORT_TREATMENTS: '/preventive-health/reports/treatment-history',
  },

  // Medications
  MEDICATIONS: {
    LIST: '/medications',
    CREATE: '/medications',
    STATISTICS: '/medications/statistics',
    TYPES: '/medications/types',
    DETAIL: (id: string | number) => `/medications/${id}`,
    UPDATE: (id: string | number) => `/medications/${id}`,
    DELETE: (id: string | number) => `/medications/${id}`,
    PURCHASE: (id: string | number) => `/medications/${id}/purchase`,
    USAGE: (id: string | number) => `/medications/${id}/usage`,
    ADJUSTMENT: (id: string | number) => `/medications/${id}/adjustment`,
    DISPOSAL: (id: string | number) => `/medications/${id}/disposal`,
    TRANSACTIONS: (id: string | number) => `/medications/${id}/transactions`,
    ALL_TRANSACTIONS: '/medications/transactions/all',
    DELETE_TRANSACTION: (id: string | number) => `/medications/transactions/${id}`,
  },
  // Attendance
  ATTENDANCE: {
    SLOTS: '/attendance/slots',
    SLOT: (id: number | string) => `/attendance/slots/${id}`,
    LIST: '/attendance',
    BULK: '/attendance/bulk',
    RECORD: (id: number | string) => `/attendance/${id}`,
    SUMMARY: '/attendance/summary',
    ROSTER: '/attendance/roster',
    ROSTER_GENERATE: '/attendance/roster/generate',
  },
} as const;
